import { clearSession, getSession, setSession } from './session-store';
import type { ApiErrorShape } from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * The rest of the server's `error` object. Some errors carry actionable
     * extras the UI shows (e.g. `openDialogs` on `channel_has_dialogs`).
     */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

const CSRF_HEADER = 'x-csrf-token';
const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

let onUnauthorized: (() => void) | null = null;

/** Wired once from the router bootstrap so a 401 anywhere redirects to /login. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function parseError(res: Response): Promise<never> {
  let body: ApiErrorShape | undefined;
  try {
    body = (await res.json()) as ApiErrorShape;
  } catch {
    // non-JSON error body (e.g. proxy failure) — fall through to a generic error below.
  }
  const code = body?.error?.code ?? 'unknown_error';
  const message = body?.error?.message ?? res.statusText ?? 'Request failed';
  if (res.status === 401) {
    clearSession();
    onUnauthorized?.();
  }
  // The server lets a temporary-password session reach nothing but the password
  // change. Reflect that in the session snapshot so the shell shows that screen
  // even if the flag was set after this tab loaded (e.g. the owner reset the password).
  if (res.status === 403 && code === 'password_change_required') {
    const me = getSession().me;
    if (me && !me.mustChangePassword) setSession({ me: { ...me, mustChangePassword: true } });
  }
  const details = { ...body?.error };
  delete details.code;
  delete details.message;
  throw new ApiError(res.status, code, message, details);
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + (url.search ?? '');
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (MUTATING.has(method)) {
    const token = getSession().csrfToken;
    if (token) headers[CSRF_HEADER] = token;
  }
  const res = await fetch(buildUrl(path, opts.query), {
    method,
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) return parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => apiFetch<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string, query?: RequestOptions['query']) => apiFetch<T>(path, { method: 'DELETE', query }),
};
